import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shuffle, Save, Loader2, ChevronLeft, ArrowUp, ArrowDown, X, Plus, Info, UserCheck,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import ToastMsg from '../../../components/common/ToastMsg';
import { cn } from '../../../utils/tailwindUtils';

const SETTINGS_URL = 'engage/assignment/settings/';
const BACKLOG_URL = 'engage/assignment/assign-unassigned/';

// Settings → Auto Assignment: new enquiries with no owner go to the people in
// the rotation, in turn (engage/assignment.py on the backend).
export default function AssignmentSettings() {
  const navigate = useNavigate();
  const { fetch: apiFetch, update: apiUpdate, create: apiCreate } = useApiMethods({ throwOnFail: true });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [staff, setStaff] = useState([]);
  const [saved, setSaved] = useState({ is_enabled: false, user_ids: [] });
  const [enabled, setEnabled] = useState(false);
  const [rotation, setRotation] = useState([]);
  const [nextUserId, setNextUserId] = useState(null);
  const [backlog, setBacklog] = useState(0);
  const [adding, setAdding] = useState('');

  const apply = (d) => {
    setStaff(d.staff || []);
    setSaved({ is_enabled: !!d.is_enabled, user_ids: d.user_ids || [] });
    setEnabled(!!d.is_enabled);
    setRotation(d.user_ids || []);
    setNextUserId(d.next_user_id ?? null);
    setBacklog(d.unassigned_open_count || 0);
  };

  useEffect(() => {
    apiFetch(SETTINGS_URL).then((d) => {
      if (d?.status === 'fail') ToastMsg('error', d.message || 'Failed to load');
      else apply(d);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byId = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);
  const notInRotation = staff.filter((s) => !rotation.includes(s.id));
  const dirty = enabled !== saved.is_enabled || rotation.join(',') !== saved.user_ids.join(',');

  const move = (i, dir) => setRotation((r) => {
    const next = [...r];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    return next;
  });

  const save = async () => {
    if (enabled && !rotation.length) { ToastMsg('error', 'Pick at least one person first'); return; }
    setSaving(true);
    try {
      const d = await apiUpdate(SETTINGS_URL, { is_enabled: enabled, user_ids: rotation });
      apply(d);
      ToastMsg('success', enabled ? 'Auto-assignment is on' : 'Auto-assignment is off');
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const assignBacklog = async () => {
    if (!window.confirm(`Assign ${backlog} open unassigned enquir${backlog === 1 ? 'y' : 'ies'} using this rotation?`)) return;
    setAssigning(true);
    try {
      const d = await apiCreate(BACKLOG_URL, {});
      const who = Object.entries(d.assigned || {}).map(([n, c]) => `${n} ${c}`).join(', ');
      ToastMsg('success', `Assigned ${d.total}${who ? ` (${who})` : ''}`);
      setBacklog(d.unassigned_open_count || 0);
      const fresh = await apiFetch(SETTINGS_URL);
      if (fresh?.status !== 'fail') apply(fresh);
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-white shadow-sm">
            <Shuffle className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Auto Assignment</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Every new enquiry without an owner goes to the next person below, in turn.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Unsaved changes</span>}
          <button onClick={save} disabled={saving || !dirty}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* On / off */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">Assign new enquiries automatically</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Covers Meta / website leads, bulk import, the New Enquiry form, partner portals and calls.
            An owner picked by hand is always kept.
          </div>
        </div>
        <button type="button" role="switch" aria-checked={enabled} onClick={() => setEnabled((v) => !v)}
          className={cn('relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors',
            enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600')}>
          <span className={cn('inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transition-transform',
            enabled ? 'translate-x-5' : 'translate-x-0.5')} />
        </button>
      </div>

      {/* Rotation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">Rotation</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {rotation.length === 1 ? 'One person: every enquiry goes to them.'
                : rotation.length > 1 ? 'Enquiries go round in this order.' : 'Nobody yet. Add staff below.'}
            </div>
          </div>
          {notInRotation.length > 0 && (
            <div className="flex items-center gap-1.5">
              <select value={adding} onChange={(e) => setAdding(e.target.value)}
                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                <option value="">Add a person…</option>
                {notInRotation.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button disabled={!adding}
                onClick={() => { setRotation((r) => [...r, Number(adding)]); setAdding(''); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-primary bg-primary/10 hover:bg-primary/20 disabled:opacity-50">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          )}
        </div>
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {rotation.map((id, i) => (
            <li key={id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              <span className="w-5 text-xs text-gray-400 text-right">{i + 1}</span>
              <span className="flex-1 text-gray-900 dark:text-white">
                {byId[id]?.name || `User ${id}`}
                {saved.is_enabled && !dirty && id === nextUserId && (
                  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    <UserCheck className="w-3 h-3" /> next enquiry
                  </span>
                )}
              </span>
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === rotation.length - 1} title="Move down"
                className="p-1 rounded text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button onClick={() => setRotation((r) => r.filter((x) => x !== id))} title="Remove"
                className="p-1 rounded text-gray-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      </div>

      {/* Backlog */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            {backlog} open enquir{backlog === 1 ? 'y has' : 'ies have'} no owner
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Auto-assignment only acts on new enquiries. Hand out the existing ones with the same rotation, oldest first.
          </div>
        </div>
        <button onClick={assignBacklog} disabled={assigning || !backlog || !saved.is_enabled || dirty}
          title={!saved.is_enabled || dirty ? 'Turn auto-assignment on and save first' : ''}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-primary hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap">
          {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
          Assign them now
        </button>
      </div>

      <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-800/40 text-[11px] text-violet-800 dark:text-violet-300 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Staff who are deactivated drop out of the rotation automatically. Each auto-assignment is noted on the
          enquiry&apos;s activity log. Lost, dropped and converted enquiries are never assigned.
        </span>
      </div>
    </div>
  );
}
